import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Resource, Unit, ResourceStatus } from '../../types';
import { API_BASE_URL } from '../../lib/utils';

interface ResourceState {
  resources: Resource[];
  units: Unit[];
  isLoading: boolean;
  error: string | null;
  filterStatus: ResourceStatus | 'all';
  filterType: string;
}

const initialState: ResourceState = {
  resources: [],
  units: [],
  isLoading: false,
  error: null,
  filterStatus: 'all',
  filterType: 'all',
};

export const fetchResources = createAsyncThunk<Resource[]>(
  'resources/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('c2_token');
      const response = await fetch(`${API_BASE_URL}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return rejectWithValue('Failed to fetch resources');
      const data = await response.json();
      return data.data;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

export const fetchUnits = createAsyncThunk<Unit[]>(
  'resources/fetchUnits',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('c2_token');
      const response = await fetch(`${API_BASE_URL}/units`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return rejectWithValue('Failed to fetch units');
      const data = await response.json();
      return data.data;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

const resourceSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setResourceFilter(state, action: PayloadAction<{ status?: ResourceStatus | 'all'; type?: string }>) {
      if (action.payload.status !== undefined) state.filterStatus = action.payload.status;
      if (action.payload.type !== undefined) state.filterType = action.payload.type;
    },
    resourceUpdated(state, action: PayloadAction<Partial<Resource> & { id: string }>) {
      const index = state.resources.findIndex((r) => r.id === action.payload.id);
      if (index !== -1) {
        state.resources[index] = { ...state.resources[index], ...action.payload };
      }
    },
    unitUpdated(state, action: PayloadAction<Partial<Unit> & { id: string }>) {
      const index = state.units.findIndex((u) => u.id === action.payload.id);
      if (index !== -1) {
        state.units[index] = { ...state.units[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResources.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resources = action.payload;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.units = action.payload;
      });
  },
});

export const { setResourceFilter, resourceUpdated, unitUpdated } = resourceSlice.actions;
export default resourceSlice.reducer;
